import type { CatalogProduct, CatalogProductVariant } from '../../../types'
import type { ResolvedProductPurchasability } from '../../../purchasability'

const PRODUCT_VARIANT_LOOKUP_SEPARATOR = '::variant::'

export const getVariantKey = (variant: CatalogProductVariant, index: number) =>
  variant.id?.trim() || variant.sku?.trim() || `variant-${index}`

export const buildVariantProductLookup = (productId: string, variantKey: string) =>
  `${productId}${PRODUCT_VARIANT_LOOKUP_SEPARATOR}${variantKey}`

const formatVariantValue = (value: unknown) => {
  if (Array.isArray(value)) return value.join(', ')
  if (typeof value === 'boolean') return value ? 'Si' : 'No'
  if (value === null || value === undefined) return ''
  return String(value)
}

const VARIANT_ATTRIBUTE_LABELS: Record<string, string> = {
  btu: 'BTU',
  capacity_liters: 'Capacità',
  consumption: 'Consumo',
  dimensions: 'Dimensioni',
  mounting: 'Montaggio',
  weight: 'Peso',
}

const formatVariantAttribute = (key: string, value: unknown) => {
  const normalizedKey = key.trim().toLowerCase().replace(/[-\s]+/g, '_')
  const formattedValue = formatVariantValue(value).trim()
  if (!formattedValue) return ''

  if (normalizedKey === 'capacity_liters') return `${formattedValue} L`
  if (normalizedKey === 'btu') return `${formattedValue} BTU`
  if (normalizedKey === 'mounting') return formattedValue

  const label =
    VARIANT_ATTRIBUTE_LABELS[normalizedKey] ??
    normalizedKey.replace(/_/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase())
  const unit = normalizedKey === 'weight' && !/\s*kg$/i.test(formattedValue) ? ' kg' : ''
  return `${label}: ${formattedValue}${unit}`
}

const getVariantEntries = (variant: CatalogProductVariant) => {
  const fields = [variant.attributes ?? {}, variant.overrides ?? {}]
  const seen = new Set<string>()

  return fields.flatMap((source) =>
    Object.entries(source).flatMap(([key, value]) => {
      const normalizedKey = key.trim().toLowerCase()
      if (!normalizedKey || seen.has(normalizedKey)) return []
      seen.add(normalizedKey)
      const formatted = formatVariantAttribute(key, value)
      return formatted ? [{ key: normalizedKey, formatted }] : []
    }),
  )
}

export const getVariantLabel = (variant: CatalogProductVariant, index: number) => {
  const capacity = variant.attributes?.capacity_liters ?? variant.overrides?.capacity_liters
  const capacityLabel = formatVariantAttribute('capacity_liters', capacity)
  if (capacityLabel) return capacityLabel

  const btu = variant.attributes?.btu ?? variant.overrides?.btu
  const btuLabel = formatVariantAttribute('btu', btu)
  if (btuLabel) return btuLabel
  return variant.name?.trim() || variant.sku?.trim() || `Variante ${index + 1}`
}

export const getVariantSummary = (variant: CatalogProductVariant) => {
  const entries = getVariantEntries(variant)
    .filter(({ key }) => key !== 'btu' && key !== 'capacity_liters')
    .map(({ formatted }) => formatted)

  if (variant.sku) entries.unshift(`SKU ${variant.sku}`)
  return entries.slice(0, 3).join(' · ')
}

/** Resolve the customer-facing price without applying VAT a second time. */
export const getVariantPrice = (product: CatalogProduct | null, variant: CatalogProductVariant | null) =>
  variant?.price ?? product?.price ?? 0

export const resolveProductDisplayPrices = (
  product: CatalogProduct | null,
  variant: CatalogProductVariant | null,
) => {
  const priceIncVat = getVariantPrice(product, variant)
  return {
    // The UI currently accepts both fields; both represent the final amount
    // so no implicit VAT transformation can alter the entered price.
    priceExVat: priceIncVat,
    priceIncVat,
  }
}

export const getVariantAvailableStock = (product: CatalogProduct | null, variant: CatalogProductVariant | null) =>
  Math.max(0, Math.trunc(variant?.quantity ?? product?.availableStock ?? product?.quantity ?? 0))

export const resolveVariantPurchasability = ({
  product,
  variant,
  base,
}: {
  product: CatalogProduct | null
  variant: CatalogProductVariant | null
  base: ResolvedProductPurchasability
}): ResolvedProductPurchasability => {
  if (!variant) return base

  const availableStock = getVariantAvailableStock(product, variant)
  const effectivePrice = getVariantPrice(product, variant)
  const productBlockingReason =
    base.reason === 'status_not_active' || base.reason === 'missing_required_fields'
      ? base.reason
      : null
  const variantInactive = variant.status === 'draft' || variant.status === 'archived'
  const invalidPrice = !Number.isFinite(effectivePrice) || effectivePrice <= 0
  const outOfStock = base.stockPolicy === 'deny-oversell' && availableStock <= 0
  const reason = productBlockingReason
    ?? (variantInactive ? 'status_not_active' : null)
    ?? (invalidPrice ? 'invalid_price' : null)
    ?? (outOfStock ? 'out_of_stock' : null)

  return {
    ...base,
    availableStock,
    isPurchasable: reason === null,
    reason,
  }
}

export const getVariantImage = (variant: CatalogProductVariant | null) =>
  variant?.mediaVariants?.large ?? variant?.mediaVariants?.medium ?? variant?.mediaVariants?.small ?? ''
